import { Link } from "react-router-dom";
import ArticleLayout from "@/components/feature/ArticleLayout";

export default function ArticleBeginners() {
  return (
    <ArticleLayout
      title="Beginner's Guide to ACBuy Spreadsheet: Your First Steps to Smarter Shopping"
      subtitle="Beginner's Guide"
      readTime="8"
      heroImgSrc="https://readdy.ai/api/search-image?query=Open%20notebook%20with%20handwritten%20notes%20next%20to%20a%20laptop%20on%20a%20bright%20desk%2C%20warm%20morning%20sunlight%2C%20learning%20and%20getting%20started%20concept%2C%20friendly%20welcoming%20atmosphere%2C%20coffee%20cup%20and%20plant%2C%20beginner%20friendly%20workspace%2C%20soft%20natural%20tones&width=1600&height=720&seq=article-beginners-hero&orientation=landscape"
      heroImgAlt="Beginner's guide to ACBuy Spreadsheet"
      description="A beginner-friendly guide to ACBuy Spreadsheet — your first steps to smarter shopping, with a complete walkthrough, tips for getting started, and common mistakes to avoid."
    >
      <p className="text-base md:text-lg text-foreground-700 leading-relaxed mb-6">
        New to the <strong>ACBuy Spreadsheet</strong> system? Welcome! This guide is designed for
        first-time users who want to understand exactly how the platform works and how to start
        saving money on purchases right away. No technical background required — if you can use
        a web browser, you can use the <strong>acbuyspreadsheet</strong> database effectively.
      </p>

      <h2 className="font-heading text-2xl md:text-3xl font-semibold text-foreground-950 mt-10 mb-4">
        What You Need Before Starting
      </h2>
      <p className="text-sm md:text-base text-foreground-700 leading-relaxed mb-4">
        Nothing! That is one of the best features of the <strong>acbuyspreadsheet</strong> system:
        you do not need to create an account, download any software, or provide any personal
        information to start using it. The platform is entirely web-based, so it works on any
        device — laptop, tablet, or phone — through a standard browser.
      </p>
      <p className="text-sm md:text-base text-foreground-700 leading-relaxed mb-6">
        Simply visit{" "}
        <a href="https://kaspreadsheet.com" target="_blank" rel="noopener nofollow" className="text-primary-600 hover:text-primary-700 font-medium">kaspreadsheet.com</a>{" "}
        to access the full database, or browse categories directly from the{" "}
        <Link to="/" className="text-primary-600 hover:text-primary-700 font-medium">ACBuy Wiki homepage</Link>.
        The spreadsheet interface is intuitive and designed for immediate use — most new users
        are comparing products within 60 seconds of landing on the page.
      </p>

      <h2 className="font-heading text-2xl md:text-3xl font-semibold text-foreground-950 mt-10 mb-4">
        Your First 5 Minutes: A Walkthrough
      </h2>

      <ol className="space-y-5 my-6">
        <li className="flex gap-4">
          <span className="w-7 h-7 flex items-center justify-center rounded-full bg-primary-500 text-background-50 text-sm font-bold shrink-0 mt-0.5">1</span>
          <div>
            <h4 className="font-semibold text-foreground-900 text-sm md:text-base mb-1">Open the Platform</h4>
            <p className="text-sm text-foreground-600 leading-relaxed">
              Go to the main site or use the category links on the ACBuy Wiki. You will see a
              clean interface with product categories clearly listed. No signup, no barriers.
            </p>
          </div>
        </li>
        <li className="flex gap-4">
          <span className="w-7 h-7 flex items-center justify-center rounded-full bg-primary-500 text-background-50 text-sm font-bold shrink-0 mt-0.5">2</span>
          <div>
            <h4 className="font-semibold text-foreground-900 text-sm md:text-base mb-1">Choose Your First Category</h4>
            <p className="text-sm text-foreground-600 leading-relaxed">
              Start with a category you are interested in — Shoes, T-Shirts, or Accessories are
              good choices for beginners because they have the most listings and show the full
              power of the <strong>acbuyspreadsheet</strong> comparison layout.
            </p>
          </div>
        </li>
        <li className="flex gap-4">
          <span className="w-7 h-7 flex items-center justify-center rounded-full bg-primary-500 text-background-50 text-sm font-bold shrink-0 mt-0.5">3</span>
          <div>
            <h4 className="font-semibold text-foreground-900 text-sm md:text-base mb-1">Scan the Spreadsheet</h4>
            <p className="text-sm text-foreground-600 leading-relaxed">
              Take a moment to read the column headers and scan a few rows. Notice how prices vary
              across suppliers for the same product. This immediate price visibility is what makes
              the spreadsheet system so powerful for beginners and experts alike.
            </p>
          </div>
        </li>
        <li className="flex gap-4">
          <span className="w-7 h-7 flex items-center justify-center rounded-full bg-primary-500 text-background-50 text-sm font-bold shrink-0 mt-0.5">4</span>
          <div>
            <h4 className="font-semibold text-foreground-900 text-sm md:text-base mb-1">Find Your First Deal</h4>
            <p className="text-sm text-foreground-600 leading-relaxed">
              Look for a product you like at a price significantly lower than the category average.
              Click through to the supplier to verify availability and make your purchase. That is it
              — you have just completed your first <strong>acbuyspreadsheet</strong> shopping session!
            </p>
          </div>
        </li>
      </ol>

      <h2 className="font-heading text-2xl md:text-3xl font-semibold text-foreground-950 mt-10 mb-4">
        Common Beginner Mistakes to Avoid
      </h2>
      <p className="text-sm md:text-base text-foreground-700 leading-relaxed mb-4">
        New users of the spreadsheet sometimes make a few predictable mistakes. The most common is
        stopping at the first decent price without scanning the full supplier list. Remember: the
        whole point of the <strong>acbuyspreadsheet</strong> system is comprehensive comparison.
        Take the extra 30 seconds to glance through all the supplier options — the savings are
        often worth many times that small time investment.
      </p>
      <p className="text-sm md:text-base text-foreground-700 leading-relaxed mb-6">
        Another common oversight is not checking back after a few days. Because the database
        refreshes every 24 hours, a product that was expensive on Monday might be reasonably
        priced by Wednesday. The spreadsheet rewards consistent, even brief, check-ins.
      </p>

      <h2 className="font-heading text-2xl md:text-3xl font-semibold text-foreground-950 mt-10 mb-4">
        Building Your Shopping Routine
      </h2>
      <p className="text-sm md:text-base text-foreground-700 leading-relaxed mb-4">
        After your first few sessions, you will naturally develop a routine that works for your
        shopping habits. Many users settle into checking the <strong>acbuyspreadsheet</strong> once
        or twice a week for their favorite categories. Others check daily during active shopping
        periods — for example, when preparing for a seasonal wardrobe update or hunting for
        specific limited-release items.
      </p>
      <p className="text-sm md:text-base text-foreground-700 leading-relaxed mb-6">
        There is no wrong approach — the system is flexible and adapts to however you prefer to
        shop. Start simple, build your comfort level, and soon you will wonder how you ever
        shopped without the spreadsheet.
      </p>

      <div className="mt-10 text-center">
        <Link
          to="/acbuyspreadsheet-guide"
          className="inline-flex items-center gap-2 px-6 py-3 bg-primary-500 text-background-50 rounded-lg text-sm font-semibold hover:bg-primary-600 transition-colors duration-200 whitespace-nowrap cursor-pointer"
        >
          <i className="ri-book-open-line"></i>
          Explore the Complete Guide
        </Link>
      </div>
    </ArticleLayout>
  );
}